import React, { useState, useEffect, useCallback } from 'react';
import { analyticsAPI, extractErrorMessage } from '../api';
import { useStore } from '../context/StoreContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Analytics = () => {
  const { selectedStore } = useStore();
  const [overview, setOverview] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [deadStock, setDeadStock] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState(null);

  const formatCurrency = (value) => {
    const num = parseFloat(value || 0);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: selectedStore?.currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  const loadAnalytics = useCallback(async () => {
    if (!selectedStore) return;
    try {
      setLoading(true);
      setError(null);
      const [overviewRes, forecastRes, deadStockRes, recommendationsRes] = await Promise.allSettled([
        analyticsAPI.getOverview(selectedStore.id),
        analyticsAPI.getForecast(selectedStore.id, { days: 30 }),
        analyticsAPI.getDeadStock(selectedStore.id, { days_threshold: 60 }),
        analyticsAPI.getRecommendations(selectedStore.id),
      ]);
      if (overviewRes.status === 'fulfilled') setOverview(overviewRes.value.data);
      if (forecastRes.status === 'fulfilled') setForecast(forecastRes.value.data);
      if (deadStockRes.status === 'fulfilled') setDeadStock(deadStockRes.value.data);
      if (recommendationsRes.status === 'fulfilled') setRecommendations(recommendationsRes.value.data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load analytics data'));
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  useEffect(() => {
    if (selectedStore) {
      loadAnalytics();
    }
  }, [selectedStore, loadAnalytics]);

  const handleRecalculate = async () => {
    if (!selectedStore || recalculating) return;
    try {
      setRecalculating(true);
      await analyticsAPI.recalculate(selectedStore.id);
      await loadAnalytics();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to recalculate analytics'));
    } finally {
      setRecalculating(false);
    }
  };

  // SVG Icon components
  const Icons = {
    forecast: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
      </svg>
    ),
    deadStock: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
        <line x1="9" y1="9" x2="15" y2="15"></line>
        <line x1="15" y1="9" x2="9" y2="15"></line>
      </svg>
    ),
    reorder: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="17 1 21 5 17 9"></polyline>
        <path d="M3 11V9a4 4 0 0 1 4-4h14"></path>
        <polyline points="7 23 3 19 7 15"></polyline>
        <path d="M21 13v2a4 4 0 0 1-4 4H3"></path>
      </svg>
    ),
    warning: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
        <line x1="12" y1="9" x2="12" y2="13"></line>
        <line x1="12" y1="17" x2="12.01" y2="17"></line>
      </svg>
    ),
    decline: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"></polyline>
        <polyline points="17 18 23 18 23 12"></polyline>
      </svg>
    ),
    confidence: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>
    ),
    refresh: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 4 23 10 17 10"></polyline>
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
      </svg>
    ),
    trendUp: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
        <polyline points="17 6 23 6 23 12"></polyline>
      </svg>
    ),
    trendDown: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"></polyline>
        <polyline points="17 18 23 18 23 12"></polyline>
      </svg>
    ),
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <span>Loading analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        {error}
        <button className="btn btn-secondary" style={{ marginLeft: '12px' }} onClick={loadAnalytics}>
          Retry
        </button>
      </div>
    );
  }

  // Extract overview data with safe defaults
  const revenueForecastAmount = overview?.revenue_forecast?.predicted_value || overview?.revenue_forecast?.total || 0;
  const revenueForecastConfidence = overview?.revenue_forecast?.confidence_score || overview?.revenue_forecast?.confidence || 0;
  const deadStockCount = overview?.dead_stock_count || 0;
  const deadStockValue = overview?.dead_stock_value || 0;
  const reorderNeeded = overview?.reorder_needed || 0;
  const stockoutWarnings = overview?.stockout_warnings || 0;
  const decliningProducts = overview?.declining_products || 0;

  // Forecast chart data — backend returns daily_predictions
  const forecastChartData = forecast?.daily_predictions || forecast?.daily_forecast || forecast?.predictions || [];

  // Dead stock items — backend returns { items: [...] }
  const deadStockItems = deadStock?.items || deadStock?.products || [];

  // Reorder suggestions
  const reorderItems = recommendations?.reorder_suggestions || [];

  // Category growth data — comes from recommendations, not overview
  const categoryGrowth = recommendations?.growth_categories || [];

  // Custom tooltip for forecast chart
  const ForecastTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: '#fff',
          border: 'none',
          borderRadius: '8px',
          padding: '10px 14px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          fontSize: '0.8125rem',
        }}>
          <p style={{ margin: 0, color: 'var(--gray-500)', marginBottom: '4px' }}>{label}</p>
          <p style={{ margin: 0, fontWeight: 600, color: 'var(--primary)' }}>
            {formatCurrency(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="dashboard-modern">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">AI Analytics</h1>
          <p className="page-subtitle">AI-powered insights and predictions for your inventory.</p>
        </div>
        <div>
          <button
            className="btn btn-primary"
            onClick={handleRecalculate}
            disabled={recalculating}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <span style={recalculating ? { animation: 'spin 1s linear infinite' } : {}}>
              {Icons.refresh}
            </span>
            {recalculating ? 'Recalculating...' : 'Recalculate'}
          </button>
        </div>
      </div>

      {/* Overview KPIs */}
      <div className="dashboard-kpi-section">
        <div className="dashboard-kpi-card">
          <div className="dashboard-kpi-icon" style={{ color: 'var(--primary)', background: 'var(--primary-50)' }}>
            {Icons.forecast}
          </div>
          <div className="dashboard-kpi-content">
            <div className="dashboard-kpi-label">Revenue Forecast (30d)</div>
            <div className="dashboard-kpi-value">{formatCurrency(revenueForecastAmount)}</div>
          </div>
        </div>

        <div className="dashboard-kpi-card">
          <div className="dashboard-kpi-icon" style={{ color: 'var(--success)', background: 'var(--success-light)' }}>
            {Icons.confidence}
          </div>
          <div className="dashboard-kpi-content">
            <div className="dashboard-kpi-label">Forecast Confidence</div>
            <div className="dashboard-kpi-value">{Math.round(revenueForecastConfidence * 100)}%</div>
          </div>
        </div>

        <div className={`dashboard-kpi-card ${deadStockCount > 0 ? 'dashboard-kpi-card-warning' : ''}`}>
          <div className="dashboard-kpi-icon" style={{ color: 'var(--warning)', background: 'var(--warning-light)' }}>
            {Icons.deadStock}
          </div>
          <div className="dashboard-kpi-content">
            <div className="dashboard-kpi-label">Dead Stock ({formatCurrency(deadStockValue)})</div>
            <div className="dashboard-kpi-value">{formatNumber(deadStockCount)} items</div>
          </div>
        </div>

        <div className="dashboard-kpi-card">
          <div className="dashboard-kpi-icon" style={{ color: 'var(--primary-light)', background: 'var(--primary-50)' }}>
            {Icons.reorder}
          </div>
          <div className="dashboard-kpi-content">
            <div className="dashboard-kpi-label">Reorder Needed</div>
            <div className="dashboard-kpi-value">{formatNumber(reorderNeeded)}</div>
          </div>
        </div>

        <div className={`dashboard-kpi-card ${stockoutWarnings > 0 ? 'dashboard-kpi-card-warning' : ''}`}>
          <div className="dashboard-kpi-icon" style={{ color: 'var(--danger)', background: 'var(--danger-light, #FEE2E2)' }}>
            {Icons.warning}
          </div>
          <div className="dashboard-kpi-content">
            <div className="dashboard-kpi-label">Stockout Warnings</div>
            <div className="dashboard-kpi-value">{formatNumber(stockoutWarnings)}</div>
          </div>
        </div>

        <div className={`dashboard-kpi-card ${decliningProducts > 0 ? 'dashboard-kpi-card-warning' : ''}`}>
          <div className="dashboard-kpi-icon" style={{ color: 'var(--gray-600)', background: 'var(--gray-100)' }}>
            {Icons.decline}
          </div>
          <div className="dashboard-kpi-content">
            <div className="dashboard-kpi-label">Declining Products</div>
            <div className="dashboard-kpi-value">{formatNumber(decliningProducts)}</div>
          </div>
        </div>
      </div>

      {/* Revenue Forecast Chart */}
      <div className="dashboard-analytics-section">
        <div className="card dashboard-chart-card" style={{ gridColumn: '1 / -1' }}>
          <div className="card-header">
            <h3 className="card-title">Revenue Forecast</h3>
            <span className="dashboard-chart-badge">Next 30 days</span>
          </div>
          <div className="card-body">
            {forecastChartData.length > 0 ? (
              <div className="chart-container" style={{ height: '350px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={forecastChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200, #E2E8F0)" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12, fill: 'var(--gray-500)' }}
                      tickFormatter={(val) => {
                        const d = new Date(val);
                        return `${d.getMonth() + 1}/${d.getDate()}`;
                      }}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: 'var(--gray-500)' }}
                      tickFormatter={(val) => {
                        if (val >= 1000) return `${(val / 1000).toFixed(0)}k`;
                        return val;
                      }}
                    />
                    <Tooltip content={<ForecastTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="predicted_revenue"
                      stroke="var(--primary, #6C63FF)"
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{ r: 5, fill: 'var(--primary, #6C63FF)' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="dashboard-empty-state">
                <div className="dashboard-empty-icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="20" x2="18" y2="10"></line>
                    <line x1="12" y1="20" x2="12" y2="4"></line>
                    <line x1="6" y1="20" x2="6" y2="14"></line>
                  </svg>
                </div>
                <div className="dashboard-empty-title">No forecast data available</div>
                <p className="dashboard-empty-text">Revenue predictions will appear here once enough sales data is collected.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dead Stock Table */}
      <div className="card dashboard-section-card">
        <div className="card-header">
          <h3 className="card-title">Dead Stock</h3>
          <span className="badge badge-warning">{deadStockItems.length} items</span>
        </div>
        <div className="card-body">
          {deadStockItems.length > 0 ? (
            <>
              <div className="table-container hidden-mobile">
                <table>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>SKU</th>
                      <th style={{ textAlign: 'center' }}>Days Without Sale</th>
                      <th style={{ textAlign: 'center' }}>Stock</th>
                      <th style={{ textAlign: 'right' }}>Value</th>
                      <th style={{ textAlign: 'center' }}>Suggested Discount</th>
                      <th style={{ textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deadStockItems.map((item, index) => (
                      <tr key={index}>
                        <td className="font-medium">{item.product_name}</td>
                        <td className="text-muted">{item.sku}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span className={`badge ${item.days_without_sale > 90 ? 'badge-danger' : 'badge-warning'}`}>
                            {item.days_without_sale}d
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>{formatNumber(item.current_stock || item.quantity_in_stock || 0)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.stock_value || 0)}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span className="badge badge-primary" style={{ background: 'var(--primary-50)', color: 'var(--primary)' }}>
                            {item.suggested_discount || 0}% off
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '4px 10px' }}>
                            Create Campaign
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="mobile-cards hidden-desktop">
                {deadStockItems.map((item, index) => (
                  <div key={index} className="mobile-card">
                    <div className="mobile-card-header">
                      <div className="mobile-card-title">{item.product_name}</div>
                      <span className={`badge ${item.days_without_sale > 90 ? 'badge-danger' : 'badge-warning'}`}>
                        {item.days_without_sale}d idle
                      </span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">SKU</span>
                      <span>{item.sku}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Stock</span>
                      <span>{formatNumber(item.current_stock || item.quantity_in_stock || 0)}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Value</span>
                      <span>{formatCurrency(item.stock_value || 0)}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Suggested Discount</span>
                      <span>{item.suggested_discount || 0}% off</span>
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '4px 10px', width: '100%' }}>
                        Create Campaign
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="dashboard-empty-state dashboard-empty-state-compact">
              <div className="dashboard-empty-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <div className="dashboard-empty-title">No dead stock detected</div>
              <p className="dashboard-empty-text">All products have been sold within the last 60 days.</p>
            </div>
          )}
        </div>
      </div>

      {/* Reorder Suggestions */}
      <div className="card dashboard-section-card">
        <div className="card-header">
          <h3 className="card-title">Reorder Suggestions</h3>
          <span className="badge badge-primary" style={{ background: 'var(--primary-50)', color: 'var(--primary)' }}>
            {reorderItems.length} products
          </span>
        </div>
        <div className="card-body">
          {reorderItems.length > 0 ? (
            <>
              <div className="table-container hidden-mobile">
                <table>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>SKU</th>
                      <th style={{ textAlign: 'center' }}>Current Stock</th>
                      <th style={{ textAlign: 'center' }}>Avg Daily Sales</th>
                      <th style={{ textAlign: 'center' }}>Days Until Stockout</th>
                      <th style={{ textAlign: 'center' }}>Suggested Qty</th>
                      <th style={{ textAlign: 'right' }}>Est. Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reorderItems.map((item, index) => {
                      const daysUntilStockout = item.days_until_stockout || 0;
                      let stockoutBadge = 'badge-success';
                      if (daysUntilStockout <= 3) stockoutBadge = 'badge-danger';
                      else if (daysUntilStockout <= 7) stockoutBadge = 'badge-warning';

                      return (
                        <tr key={index}>
                          <td className="font-medium">{item.product_name}</td>
                          <td className="text-muted">{item.sku}</td>
                          <td style={{ textAlign: 'center' }}>{formatNumber(item.current_stock)}</td>
                          <td style={{ textAlign: 'center' }}>{(item.avg_daily_sales || 0).toFixed(1)}</td>
                          <td style={{ textAlign: 'center' }}>
                            <span className={`badge ${stockoutBadge}`}>
                              {daysUntilStockout}d
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span className="font-medium">{formatNumber(item.suggested_quantity)}</span>
                          </td>
                          <td style={{ textAlign: 'right' }}>{formatCurrency(item.estimated_cost)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="mobile-cards hidden-desktop">
                {reorderItems.map((item, index) => {
                  const daysUntilStockout = item.days_until_stockout || 0;
                  let stockoutBadge = 'badge-success';
                  if (daysUntilStockout <= 3) stockoutBadge = 'badge-danger';
                  else if (daysUntilStockout <= 7) stockoutBadge = 'badge-warning';

                  return (
                    <div key={index} className="mobile-card">
                      <div className="mobile-card-header">
                        <div className="mobile-card-title">{item.product_name}</div>
                        <span className={`badge ${stockoutBadge}`}>{daysUntilStockout}d left</span>
                      </div>
                      <div className="mobile-card-row">
                        <span className="mobile-card-label">SKU</span>
                        <span>{item.sku}</span>
                      </div>
                      <div className="mobile-card-row">
                        <span className="mobile-card-label">Current Stock</span>
                        <span>{formatNumber(item.current_stock)}</span>
                      </div>
                      <div className="mobile-card-row">
                        <span className="mobile-card-label">Avg Daily Sales</span>
                        <span>{(item.avg_daily_sales || 0).toFixed(1)}</span>
                      </div>
                      <div className="mobile-card-row">
                        <span className="mobile-card-label">Suggested Qty</span>
                        <span className="font-medium">{formatNumber(item.suggested_quantity)}</span>
                      </div>
                      <div className="mobile-card-row">
                        <span className="mobile-card-label">Est. Cost</span>
                        <span>{formatCurrency(item.estimated_cost)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="dashboard-empty-state dashboard-empty-state-compact">
              <div className="dashboard-empty-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <div className="dashboard-empty-title">All stock levels are healthy</div>
              <p className="dashboard-empty-text">No reorder suggestions at this time.</p>
            </div>
          )}
        </div>
      </div>

      {/* Category Growth */}
      {categoryGrowth.length > 0 && (
        <div className="card dashboard-section-card">
          <div className="card-header">
            <h3 className="card-title">Category Growth</h3>
            <span className="dashboard-chart-badge">Trend analysis</span>
          </div>
          <div className="card-body">
            <div className="dashboard-kpi-section" style={{ marginTop: 0 }}>
              {categoryGrowth.map((cat, index) => {
                const isGrowing = (cat.growth_percentage || cat.growth || 0) >= 0;
                const growthValue = Math.abs(cat.growth_percentage || cat.growth || 0).toFixed(1);

                return (
                  <div
                    key={index}
                    className="dashboard-kpi-card"
                    style={{ minWidth: '200px' }}
                  >
                    <div
                      className="dashboard-kpi-icon"
                      style={{
                        color: isGrowing ? 'var(--success)' : 'var(--danger)',
                        background: isGrowing ? 'var(--success-light)' : 'var(--danger-light, #FEE2E2)',
                      }}
                    >
                      {isGrowing ? Icons.trendUp : Icons.trendDown}
                    </div>
                    <div className="dashboard-kpi-content">
                      <div className="dashboard-kpi-label">{cat.category_name || cat.name}</div>
                      <div className="dashboard-kpi-value" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span className={`badge ${isGrowing ? 'badge-success' : 'badge-danger'}`}>
                          {isGrowing ? '+' : '-'}{growthValue}%
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
